import React, {useCallback, useMemo, useState} from 'react';
import {
	DefaultButton,
	PrimaryButton,
	Panel,
	PanelType,
	Stack,
	Text,
	Separator,
	DetailsList,
	SelectionMode,
	IPanelProps,
	IPanelStyles,
	Spinner,
	Dialog,
	DialogType,
} from '@fluentui/react';
import {useUserContext} from 'authentication/UserContext';
import {useCommand} from 'hooks';
import {
	ParagraphUpdateInput,
	RegulatoryDocument,
	RegulatoryDocumentParagraph,
	WorkflowStatus,
} from 'types';
import {
	FileUpload,
	FileWithMetadata,
} from 'components/hookForms/ControlledFileUpload';
import {useBoolean} from '@fluentui/react-hooks';
import {
	ArrayValues,
	renderParagraph,
} from 'components/EntityList/ColumnRenderers';
import {useTranslation} from 'react-i18next';
import {useUpdateRegulatoryDocumentParagraphsMutation} from '../hooks';
import {ResultTypeFilterOption} from './DocumentCompare/CompareVersionPanel/ComparisonComponent/ResultTypeFilterOption';
import {CompareResult} from './DocumentCompare/compareDocuments/compareDocuments';
import {useGetParagraphsFormDataQuery} from 'features/RegulatoryDocuments/hooks/useGetParagraphsFormData.generated';
import {isDevEnvironment, isTestEnvironment} from 'appShell/Layout/Navigation';

interface IRegulatoryDocumentJSONForm {
	regulatoryDocumentDetails: RegulatoryDocument;
}

interface updatedMetaData {
	keywords: any[] | undefined;
	categories: any[] | undefined;
	vehicleCategories: any[] | undefined;
	driveVariants: any[] | undefined;
	keywordsRationale: string | undefined;
}

export const RegulatoryDocumentJSONForm: React.FC<
	IRegulatoryDocumentJSONForm
> = ({regulatoryDocumentDetails}) => {
	const {isVko, isAdmin} = useUserContext();
	const {data: dataForm} = useGetParagraphsFormDataQuery();

	const [isOpen, {setTrue, setFalse}] = useBoolean(false);
	const [
		saveDialogHidden,
		{setTrue: hideSaveDialog, setFalse: showSaveDialog},
	] = useBoolean(true);
	const [showChanged, {toggle: toggleChanged, setFalse: hideChanged}] =
		useBoolean(false);
	const [showEqual, {toggle: toggleEqual, setFalse: hideEqual}] =
		useBoolean(false);
	const [updatedParagraphs, setUpdatedParagraphs] = useState<updatedMetaData[]>(
		[],
	);

	const comparedParagraphsFiltered = useMemo(
		() =>
			generateComparedParagraphs(
				showChanged,
				showEqual,
				regulatoryDocumentDetails.paragraphs,
				updatedParagraphs,
			),
		[
			regulatoryDocumentDetails.paragraphs,
			updatedParagraphs,
			showChanged,
			showEqual,
		],
	);

	const dictFormInputs = useMemo(
		() => ({
			categories: extractFormInputMapping(dataForm?.categories),
			keywords: extractFormInputMapping(dataForm?.activeKeywords),
			vehicleCategories: extractFormInputMapping(dataForm?.vehicleCategories),
			driveVariants: extractFormInputMapping(dataForm?.driveVariants),
		}),
		[dataForm],
	);

	const [updateParagraphs, {loading: savingProgress}] =
		useUpdateRegulatoryDocumentParagraphsMutation();

	useCommand(
		{
			key: 'JSON',
			text: 'JSON-Import/Export',
			farCommand: true,
			onClick() {
				setTrue();
			},
			iconProps: {
				iconName: 'DiamondSolid',
			},
			hidden:
				!(isVko || isAdmin) || !(isDevEnvironment() || isTestEnvironment()),
		},
		[isVko, isAdmin],
	);

	const closePanel = () => {
		setFalse();
		setUpdatedParagraphs([]);
		hideChanged();
		hideEqual();
	};

	const downloadJson = (paragraphs: any[], fileName: string) => {
		const json = JSON.stringify(paragraphs, null, 2);

		const blob = new Blob([json], {type: 'application/json;charset=utf-8'});
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.style.display = 'none';

		document.body.appendChild(a);
		a.click();

		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const onDownloadClick = () => {
		const fileName = `input_paragraphs_${
			regulatoryDocumentDetails.id
		}_${new Date().toISOString().slice(0, 10)}.json`;

		const newPara = regulatoryDocumentDetails.paragraphs.map(x => ({
			heading: x.enumeration.trim(),
			text: x.elements
				.map(y => extractTextFromHtml(y.text ?? ''))
				.join(' ')
				.trim(),
			level: x.level,
		}));

		downloadJson(newPara, fileName);
	};

	const onDownloadClickWAttributes = () => {
		const fileName = `input_paragraphs_trace_atrributes_${
			regulatoryDocumentDetails.id
		}_${new Date().toISOString().slice(0, 10)}.json`;

		const newPara = regulatoryDocumentDetails.paragraphs.map(x => ({
			heading: x.enumeration.trim(),
			text: x.elements
				.map(y => extractTextFromHtml(y.text ?? ''))
				.join(' ')
				.trim(),
			level: x.level,
			keywords: x.keywords.map(y => ({name: y.name})),
		}));
		downloadJson(newPara, fileName);
	};

	const onSaveMetaDataClick = async () => {
		const mapNameToRef = (
			arrayOptions: any[] | undefined,
			mappingName: Record<string, any>,
		) => (arrayOptions ?? []).map(x => ({id: x.id ?? mappingName[x.name].id}));

		const inputParagraphs = {
			regulatoryDocumentId: regulatoryDocumentDetails.id,
			paragraphUpdates: regulatoryDocumentDetails.paragraphs.map((p, index) => {
				return {
					paragraphId: p.id,
					update: {
						categoryRefs: mapNameToRef(
							updatedParagraphs[index].categories,
							dictFormInputs.categories.mappingName,
						),
						keywordRefs: mapNameToRef(
							updatedParagraphs[index].keywords,
							dictFormInputs.keywords.mappingName,
						),
						vehicleCategoryRefs: mapNameToRef(
							updatedParagraphs[index].vehicleCategories,
							dictFormInputs.vehicleCategories.mappingName,
						),
						driveVariantRefs: mapNameToRef(
							updatedParagraphs[index].driveVariants,
							dictFormInputs.driveVariants.mappingName,
						),
						tagRefs: [],
						comprehensive: p.comprehensive,
						dateNewRegistration: p.dateNewRegistration,
						dateNewTypes: p.dateNewTypes,
						modelYear: p.modelYear,
						phaseIn: (p.phaseIn ?? []).map(pi => {
							return {date: pi.date, status: pi.status};
						}),
						phaseOut: (p.phaseOut ?? []).map(pi => {
							return {date: pi.date, status: pi.status};
						}),
					},
				} as ParagraphUpdateInput;
			}),
		};

		await updateParagraphs({
			variables: {
				input: inputParagraphs,
			},
		});
		window.location.reload();
	};

	const onDocumentUploadChange = async (files: FileWithMetadata[]) => {
		const updatedParagraphsNew: updatedMetaData[] = [];

		const jsonRegDocsAllFiles = (
			await Promise.all(
				files.map(async fileWithMetadata => {
					const text = await fileWithMetadata.file.text();
					const parsedObjects = JSON.parse(text) as any[];

					return parsedObjects.length ===
						regulatoryDocumentDetails.paragraphs.length
						? parsedObjects
						: null;
				}),
			)
		).filter(x => x !== null);

		const replaceIfOnlyDefined = (
			newArray: any[] | undefined,
			finalArray: any[] | undefined,
		) => {
			return newArray ?? finalArray;
		};

		const fillDefaultIfNotFound = (
			finalArray: any[] | undefined,
			originalArray: any[],
			setAllOptions: Set<string>,
		) => {
			const defaultArray = originalArray ?? [];
			const safeArray = finalArray ?? defaultArray;
			return safeArray.filter(x => setAllOptions.has(x.name));
		};

		if (jsonRegDocsAllFiles.length > 0) {
			regulatoryDocumentDetails.paragraphs.forEach((p, index) => {
				let keywords: any[] | undefined;
				let categories: any[] | undefined;
				let vehicleCategories: any[] | undefined;
				let driveVariants: any[] | undefined;
				let keywordsRationale: string | undefined;

				jsonRegDocsAllFiles.forEach(jsonRegDoc => {
					const jsonParagraph = jsonRegDoc![index];
					keywords = replaceIfOnlyDefined(jsonParagraph.keywords, keywords);
					categories = replaceIfOnlyDefined(
						jsonParagraph.categories,
						categories,
					);
					vehicleCategories = replaceIfOnlyDefined(
						jsonParagraph.vehicleCategories,
						vehicleCategories,
					);
					driveVariants = replaceIfOnlyDefined(
						jsonParagraph.driveVariants,
						driveVariants,
					);
					keywordsRationale = jsonParagraph.keywordsRationale;
				});

				updatedParagraphsNew.push({
					keywords: fillDefaultIfNotFound(
						keywords,
						p.keywords,
						dictFormInputs.keywords.setName,
					),
					categories: fillDefaultIfNotFound(
						categories,
						p.categories,
						dictFormInputs.categories.setName,
					),
					vehicleCategories: fillDefaultIfNotFound(
						vehicleCategories,
						p.vehicleCategories,
						dictFormInputs.vehicleCategories.setName,
					),
					driveVariants: fillDefaultIfNotFound(
						driveVariants,
						p.driveVariants,
						dictFormInputs.driveVariants.setName,
					),
					keywordsRationale,
				});
			});
		}

		console.log(updatedParagraphsNew);

		setUpdatedParagraphs(updatedParagraphsNew);
	};

	const onRenderBody: IPanelProps['onRenderBody'] = props => {
		return updatedParagraphs.length > 0 ? (
			<RegulatoryDocumentJSONFormResult
				comparedParagraphsFiltered={comparedParagraphsFiltered}
			/>
		) : (
			<></>
		);
	};

	const onRenderFooterContent = React.useCallback(
		() => (
			<>
				<Dialog
					hidden={saveDialogHidden}
					dialogContentProps={{
						type: DialogType.normal,
						title: 'Save metadata permanently',
						closeButtonAriaLabel: 'Close',
					}}
				>
					<Text>
						The changes will be made to regulatory document <b>permanently</b>.
						The meta-data can not rolled back to original state. Are you sure to
						continue?
					</Text>

					<Stack horizontal tokens={{childrenGap: 8}} style={{padding: 20}}>
						<DefaultButton text='Cancel' onClick={hideSaveDialog} />
						<PrimaryButton
							onClick={onSaveMetaDataClick}
							disabled={
								regulatoryDocumentDetails.workflow?.status ===
									WorkflowStatus.Finalized || updatedParagraphs.length === 0
							}
						>
							{savingProgress ? <Spinner /> : 'Save Metadata'}
						</PrimaryButton>
					</Stack>
				</Dialog>
				<Stack horizontal tokens={{childrenGap: 8}}>
					<DefaultButton text='Cancel' onClick={closePanel} />
					<PrimaryButton
						onClick={showSaveDialog}
						text='Save Metadata'
						disabled={
							regulatoryDocumentDetails.workflow?.status ===
								WorkflowStatus.Finalized || updatedParagraphs.length === 0
						}
					/>
				</Stack>
			</>
		),
		[closePanel, regulatoryDocumentDetails, updatedParagraphs],
	);

	const onRenderHeader = React.useCallback(
		() => (
			<Stack
				tokens={{childrenGap: 12}}
				style={{
					width: '100%',
					paddingLeft: 30,
					background: '#ffffff',
				}}
			>
				<h2>JSON-Import/Export</h2>
				<Text>
					<b>JSON-Export:</b> Regulatory document paragraphs can be exported as
					json file here. There are two possibilities:
				</Text>
				<Stack horizontal tokens={{childrenGap: 12}}>
					<PrimaryButton
						text='Download JSON (text only)'
						onClick={onDownloadClick}
					/>
					<DefaultButton
						text='Download JSON (with attributes)'
						onClick={onDownloadClickWAttributes}
					/>
				</Stack>
				<Separator />
				<Text>
					<b>JSON-Import</b>
				</Text>
				<FileUpload
					renderCategory={false}
					onChange={onDocumentUploadChange}
					multiple={true}
					maxFiles={10}
				/>
				<br />
				{updatedParagraphs.length > 0 && (
					<Stack horizontal>
						<ResultTypeFilterOption
							resultType={CompareResult.Equal}
							labelTranslationKey='NoChange'
							onChangeCheckbox={toggleEqual}
						/>
						<ResultTypeFilterOption
							resultType={CompareResult.Changed}
							labelTranslationKey='TextChange'
							onChangeCheckbox={toggleChanged}
						/>
						<Text style={{margin: 2, padding: 6, border: '1px solid #e1e1e1'}}>
							{`${comparedParagraphsFiltered.length} Segmente`}
						</Text>
					</Stack>
				)}
				<br />
			</Stack>
		),
		[updatedParagraphs, comparedParagraphsFiltered, dictFormInputs],
	);

	const panelStyles: Partial<IPanelStyles> = {
		content: {
			zIndex: 1,
		},
		footer: {
			background: '#ffffff',
			position: 'relative',
			zIndex: 2,
		},
	};

	return (
		<Panel
			isOpen={isOpen}
			onDismiss={closePanel}
			closeButtonAriaLabel='Close'
			onRenderBody={onRenderBody}
			onRenderFooterContent={onRenderFooterContent}
			onRenderHeader={onRenderHeader}
			type={PanelType.large}
			styles={panelStyles}
		/>
	);
};

interface IRegulatoryDocumentJSONFormResutlt {
	comparedParagraphsFiltered: any[];
}

const RegulatoryDocumentJSONFormResult: React.FC<
	IRegulatoryDocumentJSONFormResutlt
> = ({comparedParagraphsFiltered}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'ParagraphsList',
	});
	const renderComparedParagraph = useCallback(
		(comparedParagraph: any, index: number | undefined) => {
			const renderRow = (c1: any, c2: any, c3: any, backgroundColor = '') => {
				return (
					<tr style={{backgroundColor}}>
						<td style={{width: '20%'}}>{c1}</td>
						<td style={{width: '40%'}}>{c2}</td>
						<td style={{width: '40%'}}>{c3}</td>
					</tr>
				);
			};

			const renderHeaderRow = (c: string[]) => {
				const formatHeader = (name: string) => {
					return (
						<div style={{height: 35}}>
							<b>{name}</b>
						</div>
					);
				};
				const headers = c.map(x => formatHeader(x));

				return renderRow(headers[0], headers[1], headers[2]);
			};

			const renderArrayRow = (
				c1: string,
				c2: any[],
				c3: any[],
				changed = false,
			) => {
				const c3Temp = c3.map(x => {
					const confidence = x.confidence;
					if (confidence) {
						return `${x.name} -> [ ${confidence} ]`;
					}
					return x.name;
				});
				const backgroundColor = changed ? '#f6d7d7' : '';
				return renderRow(
					c1,
					<ArrayValues array={c2} />,
					<ArrayValues array={c3Temp} />,
					backgroundColor,
				);
			};

			const tableStyle = {
				border: '1px solid black',
				padding: '6px 8px',
				width: '90%',
				alignSelf: 'center',
			};

			return (
				<Stack>
					<div
						style={{
							width: '90%',
							overflowWrap: 'anywhere',
							whiteSpace: 'normal',
							flex: 1,
						}}
					>
						{renderParagraph(
							{...comparedParagraph, level: 0} as RegulatoryDocumentParagraph,
							t,
						)}
					</div>
					<table style={tableStyle}>
						<tbody>
							{renderHeaderRow(['Attributes', 'Current', 'Updated'])}
							{renderArrayRow(
								t('Categories'),
								comparedParagraph.categories,
								comparedParagraph.updatedData.categories,
								comparedParagraph.changedData.categories,
							)}
							{renderArrayRow(
								t('Keywords'),
								comparedParagraph.keywords,
								comparedParagraph.updatedData.keywords,
								comparedParagraph.changedData.keywords,
							)}
							{renderArrayRow(
								t('VehicleCategory'),
								comparedParagraph.vehicleCategories,
								comparedParagraph.updatedData.vehicleCategories,
								comparedParagraph.changedData.vehicleCategories,
							)}
							{renderArrayRow(
								t('DriveVariants'),
								comparedParagraph.driveVariants,
								comparedParagraph.updatedData.driveVariants,
								comparedParagraph.changedData.driveVariants,
							)}
						</tbody>
					</table>
					<br />
					{comparedParagraph.updatedData.keywordsRationale ? (
						<div>
							<i>
								{'*Keyword-Rationale: ' +
									comparedParagraph.updatedData.keywordsRationale}
							</i>
						</div>
					) : (
						<></>
					)}
				</Stack>
			);
		},
		[],
	);

	const columns = [
		{
			key: 'position',
			name: t('Position'),
			fieldName: 'position',
			minWidth: 50,
			maxWidth: 50,
		},
		{
			key: 'p-paragraph',
			name: t('Paragraph'),
			fieldName: 'name',
			minWidth: 400,
			onRender: (item: any, index: number | undefined) =>
				renderComparedParagraph(item, index),
		},
	];
	return (
		<Stack
			tokens={{childrenGap: 8}}
			styles={{
				root: {
					border: '1px solid #e1e1e1',
					margin: '0 50px 40px 50px',
					borderRadius: 4,
				},
			}}
		>
			<DetailsList
				items={comparedParagraphsFiltered}
				columns={columns}
				selectionMode={SelectionMode.none}
				onShouldVirtualize={() => true}
			/>
		</Stack>
	);
};

const extractFormInputMapping = (array: any[] | null | undefined) => {
	const convertArraytoSet = (array: any[]) => {
		return new Set(array.map(x => x.name));
	};

	const mapByName = (array: any[]) => {
		return array.reduce((acc, item) => {
			acc[item.name] = item;
			return acc;
		}, {} as Record<string, any>);
	};

	return {
		setName: convertArraytoSet(array ?? []),
		mappingName: mapByName(array ?? []),
	};
};

const extractTextFromHtml = (html: string) => {
	const temp = document.createElement('div');
	temp.innerHTML = html;
	return temp.textContent ?? '';
};

const generateComparedParagraphs = (
	showChanged: boolean,
	showEqual: boolean,
	paragraphs: RegulatoryDocumentParagraph[],
	updatedParagraphs: updatedMetaData[],
) => {
	if (updatedParagraphs.length === 0) {
		return [];
	}

	const checkIfChanged = (currentArray: any[], updatedArray: any[]) => {
		const sa = new Set(currentArray.map(x => x.name));
		const sb = new Set(updatedArray.map(x => x.name));

		if (sa.size !== sb.size) return true;

		for (const x of sa) {
			if (!sb.has(x)) return true;
		}
		return false;
	};

	const comparedParagraphs = paragraphs.map((p, index) => {
		const updatedData: updatedMetaData = {
			categories: updatedParagraphs[index].categories!,
			keywords: updatedParagraphs[index].keywords!,
			vehicleCategories: updatedParagraphs[index].vehicleCategories!,
			driveVariants: updatedParagraphs[index].driveVariants!,
			keywordsRationale:
				updatedParagraphs[index].keywordsRationale ?? undefined,
		};
		const changedData = {
			categories: checkIfChanged(p.categories, updatedData.categories!),
			keywords: checkIfChanged(p.keywords, updatedData.keywords!),
			vehicleCategories: checkIfChanged(
				p.vehicleCategories,
				updatedData.vehicleCategories!,
			),
			driveVariants: checkIfChanged(
				p.driveVariants,
				updatedData.driveVariants!,
			),
		};
		return {...p, updatedData, changedData};
	});

	return comparedParagraphs.filter(x => {
		if (showChanged && !showEqual) {
			return (
				x.changedData.categories ||
				x.changedData.keywords ||
				x.changedData.vehicleCategories ||
				x.changedData.driveVariants
			);
		}
		if (!showChanged && showEqual) {
			return (
				!x.changedData.categories &&
				!x.changedData.keywords &&
				!x.changedData.vehicleCategories &&
				!x.changedData.driveVariants
			);
		}
		return true;
	});
};
